/**
 * AI Visibility Impact Service
 * 
 * Calculates LLM Visibility Score based on:
 * - Connection quality (completed flows, relevance, activity)
 * - Mutual Respect (consistency, respectful interactions)
 * - Expert Status (level, topics, positioning)
 * - Network Clubs (max 2 active, relevant, participation)
 * - Collaborations (tags, mentions, co-connections, video shares, video connections)
 */

import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc, Timestamp, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { 
  AIVisibilityImpactData, 
  AIVisibilityScore, 
  AIVisibilityFactors,
  FactorStatus,
  TrendDirection,
  ConnectionFactor,
  MutualRespectFactor,
  ExpertStatusFactor,
  NetworkClubFactor,
  CollaborationFactor,
  MutualGrowthPartner
} from '../types/aiVisibilityImpact.types';
import { getExpertStatus } from './expertStatusService';
import { ExpertStatus } from '../types/expertStatus.types';
import contentFilterService from './contentFilterService';
import { extractYouTubeVideoId, extractVimeoVideoId, normalizeVideoIdentifier } from './videoEngagementService';

const DAYS_ACTIVE_THRESHOLD = 30; // Connections active in last 30 days
const MAX_ACTIVE_CLUBS = 2;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Get cached AI Visibility Impact data
 */
async function getCachedImpact(userId: string): Promise<AIVisibilityImpactData | null> {
  try {
    const cacheRef = doc(db, 'aiVisibilityCache', userId);
    const cacheDoc = await getDoc(cacheRef);
    
    if (cacheDoc.exists()) {
      const data = cacheDoc.data();
      const cachedAt = data.cachedAt?.toDate?.() || new Date(data.cachedAt);
      const age = Date.now() - cachedAt.getTime();
      
      // Check if cache is still valid (within TTL)
      if (age < CACHE_TTL) {
        console.log(`📦 AI Visibility Impact from cache (age: ${Math.round(age / 1000 / 60)} min)`);
        return data.data as AIVisibilityImpactData;
      } else {
        console.log(`⏰ AI Visibility Impact cache expired (age: ${Math.round(age / 1000 / 60 / 60)} hours)`);
      }
    }
    return null;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
}

/**
 * Save AI Visibility Impact data to cache
 * Also stores current score as previousScore for next calculation
 */
async function saveToCache(userId: string, data: AIVisibilityImpactData): Promise<void> {
  try {
    const cacheRef = doc(db, 'aiVisibilityCache', userId);
    
    // Get current cached data to preserve previousScore
    const cacheDoc = await getDoc(cacheRef);
    let previousScore: number | null = null;
    
    if (cacheDoc.exists()) {
      const cachedData = cacheDoc.data().data as AIVisibilityImpactData | undefined;
      if (cachedData?.score?.score !== undefined) {
        previousScore = cachedData.score.score;
      }
    }
    
    // Update data with previousScore for trend calculation
    const dataToSave: AIVisibilityImpactData = {
      ...data,
      score: {
        ...data.score,
        previousScore: previousScore
      }
    };
    
    await setDoc(cacheRef, {
      data: dataToSave,
      cachedAt: serverTimestamp(),
      userId
    }, { merge: true });
    console.log('💾 AI Visibility Impact saved to cache');
  } catch (error) {
    console.error('Error saving to cache:', error);
    // Don't throw - caching is optional
  }
}

/**
 * Invalidate cache for a user (call after important actions)
 */
export async function invalidateAIVisibilityCache(userId: string): Promise<void> {
  try {
    const cacheRef = doc(db, 'aiVisibilityCache', userId);
    await setDoc(cacheRef, {
      cachedAt: new Date(0), // Set to epoch to force expiration
      userId
    }, { merge: true });
    console.log('🗑️ AI Visibility Impact cache invalidated');
  } catch (error) {
    console.error('Error invalidating cache:', error);
  }
}

/**
 * Calculate AI Visibility Impact for a user
 * Uses cache if available and fresh (< 24 hours old)
 */
export async function calculateAIVisibilityImpact(userId: string): Promise<AIVisibilityImpactData> {
  // Check cache first
  const cached = await getCachedImpact(userId);
  if (cached) {
    return cached;
  }

  // Cache miss or expired - calculate fresh
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

  // Load all factors in parallel
  const [
    connectionsFactor,
    mutualRespectFactor,
    expertStatusFactor,
    networkClubsFactor,
    collaborationsFactor,
    previousScore
  ] = await Promise.all([
    calculateConnectionFactor(userId, thirtyDaysAgo, sixtyDaysAgo),
    calculateMutualRespectFactor(userId, thirtyDaysAgo),
    calculateExpertStatusFactor(userId),
    calculateNetworkClubFactor(userId, thirtyDaysAgo),
    calculateCollaborationFactor(userId, thirtyDaysAgo),
    getPreviousScore(userId)
  ]);

  const factors: AIVisibilityFactors = {
    connections: connectionsFactor,
    mutualRespect: mutualRespectFactor,
    expertStatus: expertStatusFactor,
    networkClubs: networkClubsFactor,
    collaborations: collaborationsFactor
  };

  // Calculate overall score (0-100)
  const score = calculateOverallScore(factors);
  
  // Calculate trend
  const trend = calculateTrend(score, previousScore);
  
  const visibilityScore: AIVisibilityScore = {
    score,
    previousScore,
    trend,
    trendDays: 30, // We track 30 days
    lastCalculated: now
  };

  // Get mutual growth partners
  const mutualGrowth = await getMutualGrowthPartners(userId, thirtyDaysAgo);

  // Generate explanation
  const explanation = generateExplanation(factors, score);

  const result: AIVisibilityImpactData = {
    score: visibilityScore,
    factors,
    mutualGrowth,
    explanation
  };

  // Save to cache (async, don't wait)
  saveToCache(userId, result).catch(err => {
    console.error('Failed to save cache:', err);
  });

  return result;
}

/**
 * Calculate Connection Factor
 * - Only counts completed connection flows
 * - Must be relevant (expertise/sector/region overlap)
 * - Must be active (interaction in last 30-60 days)
 */
async function calculateConnectionFactor(
  userId: string,
  thirtyDaysAgo: Date,
  sixtyDaysAgo: Date
): Promise<ConnectionFactor> {
  try {
    // Get all connections where user is participant
    const connectionsQuery1 = query(
      collection(db, 'connections'),
      where('user1Id', '==', userId)
    );
    const connectionsQuery2 = query(
      collection(db, 'connections'),
      where('user2Id', '==', userId)
    );

    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(connectionsQuery1),
      getDocs(connectionsQuery2)
    ]);

    const allConnections = [
      ...snapshot1.docs.map(doc => ({ id: doc.id, ...doc.data(), isUser1: true, otherUserId: doc.data().user2Id })),
      ...snapshot2.docs.map(doc => ({ id: doc.id, ...doc.data(), isUser1: false, otherUserId: doc.data().user1Id }))
    ] as Array<{
      id: string;
      status?: string;
      phase?: string;
      phaseStatus?: string;
      updatedAt?: any;
      otherUserId?: string;
      [key: string]: any;
    }>;

    // Filter: only completed flows
    const completedConnections = allConnections.filter(
      conn => conn.status === 'accepted' && conn.phase === 'chat' && conn.phaseStatus === 'done'
    );

    // Count active connections (interaction in last 30-60 days)
    const activeConnections = completedConnections.filter(conn => {
      const updatedAt = conn.updatedAt?.toDate?.() || conn.updatedAt;
      if (!updatedAt) return false;
      return updatedAt >= thirtyDaysAgo;
    });

    // For relevance, we'd need to check expertise/sector/region overlap
    // For now, we'll use a simplified check: if connection has been active, it's relevant
    const relevantConnections = activeConnections; // Simplified: active = relevant

    const count = completedConnections.length;
    const activeCount = activeConnections.length;
    const relevantCount = relevantConnections.length;

    // Determine status
    let status: FactorStatus = 'laag';
    if (count >= 10 && activeCount >= 5) {
      status = 'groeiend';
    } else if (count >= 5 && activeCount >= 2) {
      status = 'stabiel';
    }

    return {
      status,
      count,
      activeCount,
      relevantCount,
      description: `${count} connecties, ${activeCount} actief, ${relevantCount} relevant`
    };
  } catch (error) {
    console.error('Error calculating connection factor:', error);
    return {
      status: 'laag',
      count: 0,
      activeCount: 0,
      relevantCount: 0,
      description: 'Geen data beschikbaar'
    };
  }
}

/**
 * Calculate Mutual Respect Factor
 * - Consistent respect score
 * - Respectful interactions
 * - No negative content (CleanMind filter)
 */
async function calculateMutualRespectFactor(
  userId: string,
  thirtyDaysAgo: Date
): Promise<MutualRespectFactor> {
  try {
    // Get user profile for name
    const userProfileRef = doc(db, 'userProfiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);
    const userName = userProfileSnap.exists() ? (userProfileSnap.data()?.displayName || 'User') : 'User';

    // Check for respect relations
    const respectQuery = query(
      collection(db, 'respectRelations'),
      where('fromUserId', '==', userId)
    );
    const respectSnapshot = await getDocs(respectQuery);

    const hasRespectfulInteractions = respectSnapshot.size > 0;

    // Check for mutual respect (bidirectional)
    const mutualRespectQuery = query(
      collection(db, 'respectRelations'),
      where('toUserId', '==', userId)
    );
    const mutualRespectSnapshot = await getDocs(mutualRespectQuery);

    const hasConsistentRespect = mutualRespectSnapshot.size > 0;

    // Check for negative content using CleanMind filter
    let hasNegativeContent = false;
    try {
      // Get user's posts from last 60 days
      const postsQuery = query(
        collection(db, 'communityPosts'),
        where('userId', '==', userId),
        where('timestamp', '>=', thirtyDaysAgo),
        orderBy('timestamp', 'desc'),
        limit(20) // Check last 20 posts
      );
      const postsSnapshot = await getDocs(postsQuery);

      // Analyze posts for negative content (filter by date in code)
      for (const postDoc of postsSnapshot.docs) {
        const postData = postDoc.data();
        const postTimestamp = postData.timestamp?.toDate?.() || postData.timestamp;
        if (postTimestamp && postTimestamp < thirtyDaysAgo) continue; // Skip old posts
        
        const content = postData.content || '';
        
        if (content.trim()) {
          const filterResult = await contentFilterService.analyzeContent(
            content,
            userId,
            userName,
            'post'
          );
          
          // If confidence > 0.5, consider it negative content
          if (filterResult.confidence > 0.5 && !filterResult.isApproved) {
            hasNegativeContent = true;
            break; // Found negative content, no need to check more
          }
        }
      }
    } catch (filterError) {
      console.warn('Error checking negative content:', filterError);
      // Don't fail the whole calculation if filter check fails
    }

    let status: FactorStatus = 'laag';
    if (hasConsistentRespect && hasRespectfulInteractions && !hasNegativeContent) {
      status = 'groeiend';
    } else if (hasRespectfulInteractions && !hasNegativeContent) {
      status = 'stabiel';
    }

    return {
      status,
      hasConsistentRespect,
      hasRespectfulInteractions,
      hasNegativeContent,
      description: hasConsistentRespect 
        ? 'Consistente wederkerigheid' 
        : hasRespectfulInteractions 
        ? 'Respectvolle interacties' 
        : 'Beperkte interactie'
    };
  } catch (error) {
    console.error('Error calculating mutual respect factor:', error);
    return {
      status: 'laag',
      hasConsistentRespect: false,
      hasRespectfulInteractions: false,
      hasNegativeContent: false,
      description: 'Geen data beschikbaar'
    };
  }
}

/**
 * Calculate Expert Status Factor
 * - Expert level
 * - Clear expertise topics
 * - Consistent positioning
 * - Behavior matches expertise
 */
async function calculateExpertStatusFactor(userId: string): Promise<ExpertStatusFactor> {
  try {
    const expertStatus = await getExpertStatus(userId);
    
    if (!expertStatus) {
      return {
        status: 'laag',
        level: 'none',
        hasClearTopics: false,
        hasConsistentPositioning: false,
        behaviorMatchesExpertise: false,
        description: 'Geen expert status'
      };
    }

    // Check if user has clear topics (would need user profile data)
    // Simplified: if expert level is verified+, assume clear topics
    const hasClearTopics = expertStatus.level !== 'none';
    
    // Consistent positioning: if health is good, positioning is consistent
    const hasConsistentPositioning = expertStatus.health >= 70;
    
    // Behavior matches expertise: if conversations are happening, behavior matches
    const behaviorMatchesExpertise = expertStatus.conversationsLast30Days > 0;

    let status: FactorStatus = 'laag';
    if (expertStatus.level === 'top_expert' || expertStatus.level === 'expert') {
      status = 'groeiend';
    } else if (expertStatus.level === 'verified') {
      status = 'stabiel';
    }

    return {
      status,
      level: expertStatus.level,
      hasClearTopics,
      hasConsistentPositioning,
      behaviorMatchesExpertise,
      description: `Expert level: ${expertStatus.level}, Health: ${expertStatus.health}%`
    };
  } catch (error) {
    console.error('Error calculating expert status factor:', error);
    return {
      status: 'laag',
      level: 'none',
      hasClearTopics: false,
      hasConsistentPositioning: false,
      behaviorMatchesExpertise: false,
      description: 'Geen data beschikbaar'
    };
  }
}

/**
 * Calculate Network Club Factor
 * - Max 2 active clubs
 * - Must be relevant (content/region)
 * - Active participation required
 */
async function calculateNetworkClubFactor(
  userId: string,
  thirtyDaysAgo: Date
): Promise<NetworkClubFactor> {
  try {
    // Get user's entity memberships
    const membershipsQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userId)
    );
    const membershipsSnapshot = await getDocs(membershipsQuery);

    const allMemberships = membershipsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Array<{
      id: string;
      entityId?: string;
      [key: string]: any;
    }>;
    
    // Get entities and filter for networkclubs, masterminds, communities
    const activeClubs: string[] = [];
    const relevantClubs: string[] = [];
    
    // Check each membership to see if it's a club/mastermind/community
    for (const membership of allMemberships.slice(0, MAX_ACTIVE_CLUBS * 2)) { // Check more to find active ones
      try {
        if (!membership.entityId) continue;
        const entityRef = doc(db, 'entities', membership.entityId);
        const entitySnap = await getDoc(entityRef);
        
        if (entitySnap.exists()) {
          const entity = entitySnap.data();
          const entityType = entity.type;
          
          // Check if it's a networkclub, mastermind, or community
          // Note: entities collection uses 'networkclub' type, but we also check for mastermind/community
          // For now, we'll check if entity has isActive flag (for networkclubs)
          if (entityType === 'networkclub' || entity.type === 'mastermind' || entity.type === 'community') {
            if (entity.isActive) {
              activeClubs.push(membership.entityId);
              relevantClubs.push(membership.entityId); // Simplified: active = relevant
            }
          }
        }
      } catch (err) {
        // Skip if entity doesn't exist
        continue;
      }
    }

    const activeClubsCount = Math.min(activeClubs.length, MAX_ACTIVE_CLUBS);
    const relevantClubsCount = relevantClubs.length;
    const hasActiveParticipation = activeClubsCount > 0;

    let status: FactorStatus = 'laag';
    if (activeClubsCount === MAX_ACTIVE_CLUBS && hasActiveParticipation) {
      status = 'groeiend';
    } else if (activeClubsCount >= 1 && hasActiveParticipation) {
      status = 'stabiel';
    }

    return {
      status,
      activeClubs: activeClubsCount,
      relevantClubs: relevantClubsCount,
      hasActiveParticipation,
      description: `${activeClubsCount} actieve club${activeClubsCount !== 1 ? 's' : ''}`
    };
  } catch (error) {
    console.error('Error calculating network club factor:', error);
    return {
      status: 'laag',
      activeClubs: 0,
      relevantClubs: 0,
      hasActiveParticipation: false,
      description: 'Geen data beschikbaar'
    };
  }
}

/**
 * Calculate Collaboration Factor
 * - Tagged by others
 * - Mentions
 * - Co-connections
 * - Joint club activities
 * - Video shares (posts containing user's videos)
 * - Video connections (connections from video posts)
 */
async function calculateCollaborationFactor(
  userId: string,
  thirtyDaysAgo: Date
): Promise<CollaborationFactor> {
  try {
    const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);

    // Get posts where user is tagged/mentioned
    const postsQuery = query(
      collection(db, 'communityPosts'),
      where('mentionedUserIds', 'array-contains', userId),
      orderBy('timestamp', 'desc')
    );
    const postsSnapshot = await getDocs(postsQuery);

    const recentPosts = postsSnapshot.docs.filter(doc => {
      const data = doc.data();
      const timestamp = data.timestamp?.toDate?.() || data.timestamp;
      return timestamp && timestamp >= thirtyDaysAgo;
    });

    const taggedCount = recentPosts.length;
    const mentionsCount = taggedCount; // Simplified: tagged = mentioned

    // Get co-connections (shared connections)
    // This would require checking all connections and finding overlaps
    // Simplified for now
    const coConnectionsCount = 0; // Would need complex calculation

    // Get joint club activities
    // Check if user is in same clubs as others
    const jointActivity = 0; // Would need complex calculation

    // NIEUW: Get video shares and connections
    // Get all videos uploaded by this user
    const userVideosQuery = query(
      collection(db, 'videos'),
      where('userId', '==', userId)
    );
    const userVideosSnapshot = await getDocs(userVideosQuery);
    const userVideos = userVideosSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Array<{
      id: string;
      videoUrl?: string;
      platformId?: string;
      platform?: 'youtube' | 'vimeo' | 'upload';
    }>;

    // Create set of video identifiers for quick lookup
    const userVideoIdentifiers = new Set<string>();
    userVideos.forEach(video => {
      const identifier = normalizeVideoIdentifier(video);
      if (identifier) {
        userVideoIdentifiers.add(identifier);
      }
      if (video.videoUrl) {
        userVideoIdentifiers.add(video.videoUrl);
      }
    });

    // Get all posts from last 30 days
    const allPostsQuery = query(
      collection(db, 'communityPosts'),
      where('timestamp', '>=', thirtyDaysAgoTimestamp),
      orderBy('timestamp', 'desc')
    );
    const allPostsSnapshot = await getDocs(allPostsQuery);

    let videoSharesCount = 0;
    let videoConnectionsCount = 0;
    const videoPostIds: string[] = [];

    // Check each post to see if it contains user's videos
    for (const postDoc of allPostsSnapshot.docs) {
      const postData = postDoc.data();
      let containsUserVideo = false;

      // Check content for YouTube/Vimeo links
      if (postData.content) {
        const youtubeId = extractYouTubeVideoId(postData.content);
        const vimeoId = extractVimeoVideoId(postData.content);
        
        if (youtubeId && userVideoIdentifiers.has(`youtube:${youtubeId}`)) {
          containsUserVideo = true;
        }
        if (vimeoId && userVideoIdentifiers.has(`vimeo:${vimeoId}`)) {
          containsUserVideo = true;
        }
      }

      // Check linkPreviews for video URLs
      if (!containsUserVideo && postData.linkPreviews && Array.isArray(postData.linkPreviews)) {
        for (const preview of postData.linkPreviews) {
          if (preview.url) {
            if (userVideoIdentifiers.has(preview.url)) {
              containsUserVideo = true;
              break;
            }
            // Also check normalized identifiers
            const youtubeId = extractYouTubeVideoId(preview.url);
            const vimeoId = extractVimeoVideoId(preview.url);
            if ((youtubeId && userVideoIdentifiers.has(`youtube:${youtubeId}`)) ||
                (vimeoId && userVideoIdentifiers.has(`vimeo:${vimeoId}`))) {
              containsUserVideo = true;
              break;
            }
          }
        }
      }

      // Check attachments for video URLs
      if (!containsUserVideo && postData.attachments && Array.isArray(postData.attachments)) {
        for (const attachment of postData.attachments) {
          if (attachment.type === 'video' && attachment.url && userVideoIdentifiers.has(attachment.url)) {
            containsUserVideo = true;
            break;
          }
        }
      }

      if (containsUserVideo) {
        videoPostIds.push(postDoc.id);
        
        // Count shares (if post has shares field)
        if (typeof postData.shares === 'number') {
          videoSharesCount += postData.shares;
        }

        // Count connections from this post (if postId is tracked in connections)
        try {
          const connectionsFromPostQuery = query(
            collection(db, 'connections'),
            where('postId', '==', postDoc.id)
          );
          const connectionsSnapshot = await getDocs(connectionsFromPostQuery);
          videoConnectionsCount += connectionsSnapshot.size;
        } catch (err) {
          // Connections collection might not have postId field, skip silently
          console.debug('Could not query connections by postId:', err);
        }
      }
    }

    // Calculate total collaborations (including video metrics)
    const totalCollaborations = taggedCount + mentionsCount + 
                                coConnectionsCount + jointActivity + 
                                videoSharesCount + videoConnectionsCount;

    let status: FactorStatus = 'laag';
    if (totalCollaborations >= 10) {
      status = 'groeiend';
    } else if (totalCollaborations >= 3) {
      status = 'stabiel';
    }

    // Build description
    const parts: string[] = [];
    if (taggedCount > 0) parts.push(`${taggedCount} tags`);
    if (mentionsCount > 0 && mentionsCount !== taggedCount) parts.push(`${mentionsCount} mentions`);
    if (videoSharesCount > 0) parts.push(`${videoSharesCount} video shares`);
    if (videoConnectionsCount > 0) parts.push(`${videoConnectionsCount} video connections`);
    
    const description = parts.length > 0 
      ? parts.join(', ')
      : 'Geen collaboraties';

    return {
      status,
      taggedCount,
      mentionsCount,
      coConnectionsCount,
      jointActivity,
      videoSharesCount,
      videoConnectionsCount,
      description
    };
  } catch (error) {
    console.error('Error calculating collaboration factor:', error);
    return {
      status: 'laag',
      taggedCount: 0,
      mentionsCount: 0,
      coConnectionsCount: 0,
      jointActivity: 0,
      videoSharesCount: 0,
      videoConnectionsCount: 0,
      description: 'Geen data beschikbaar'
    };
  }
}

/**
 * Calculate overall score (0-100) from factors
 */
function calculateOverallScore(factors: AIVisibilityFactors): number {
  let score = 0;
  let maxScore = 0;

  // Connections: 30 points max
  const connectionsScore = factors.connections.status === 'groeiend' ? 30 :
                           factors.connections.status === 'stabiel' ? 20 : 10;
  score += connectionsScore;
  maxScore += 30;

  // Mutual Respect: 20 points max
  const respectScore = factors.mutualRespect.status === 'groeiend' ? 20 :
                       factors.mutualRespect.status === 'stabiel' ? 15 : 5;
  score += respectScore;
  maxScore += 20;

  // Expert Status: 25 points max
  const expertScore = factors.expertStatus.status === 'groeiend' ? 25 :
                      factors.expertStatus.status === 'stabiel' ? 15 : 5;
  score += expertScore;
  maxScore += 25;

  // Network Clubs: 15 points max
  const clubsScore = factors.networkClubs.status === 'groeiend' ? 15 :
                     factors.networkClubs.status === 'stabiel' ? 10 : 5;
  score += clubsScore;
  maxScore += 15;

  // Collaborations: 10 points max (base score)
  const collabScore = factors.collaborations.status === 'groeiend' ? 10 :
                     factors.collaborations.status === 'stabiel' ? 7 : 3;
  score += collabScore;
  maxScore += 10;

  // Extra boost voor video engagement (max 5 extra punten)
  // Video shares en connections tonen dat content impact heeft
  const videoEngagement = factors.collaborations.videoSharesCount + 
                          factors.collaborations.videoConnectionsCount;
  if (videoEngagement > 0) {
    // Geef extra punten op basis van video engagement
    // 1 punt per 3 shares/connections, max 5 punten
    const videoBoost = Math.min(5, Math.floor(videoEngagement / 3));
    score += videoBoost;
    maxScore += 5;
  }

  // Normalize to 0-100
  return Math.round((score / maxScore) * 100);
}

/**
 * Calculate trend direction
 */
function calculateTrend(currentScore: number, previousScore: number | null): TrendDirection {
  if (previousScore === null) return 'stable';
  if (currentScore > previousScore + 5) return 'up';
  if (currentScore < previousScore - 5) return 'down';
  return 'stable';
}

/**
 * Get previous score for trend calculation
 * Retrieves from cache document (last calculated score)
 */
async function getPreviousScore(userId: string): Promise<number | null> {
  try {
    const cacheRef = doc(db, 'aiVisibilityCache', userId);
    const cacheDoc = await getDoc(cacheRef);
    
    if (cacheDoc.exists()) {
      const data = cacheDoc.data();
      const cachedData = data.data as AIVisibilityImpactData | undefined;
      if (cachedData?.score?.score !== undefined) {
        return cachedData.score.score;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting previous score:', error);
    return null;
  }
}

/**
 * Get mutual growth partners
 * Users with shared connections and clubs, sorted by relevance
 */
async function getMutualGrowthPartners(
  userId: string,
  thirtyDaysAgo: Date
): Promise<MutualGrowthPartner[]> {
  try {
    // Get user's connections
    const connectionsQuery1 = query(
      collection(db, 'connections'),
      where('user1Id', '==', userId),
      where('status', '==', 'accepted')
    );
    const connectionsQuery2 = query(
      collection(db, 'connections'),
      where('user2Id', '==', userId),
      where('status', '==', 'accepted')
    );

    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(connectionsQuery1),
      getDocs(connectionsQuery2)
    ]);

    const userConnections = new Set<string>();
    snapshot1.docs.forEach(doc => {
      const data = doc.data();
      if (data.user2Id) userConnections.add(data.user2Id);
    });
    snapshot2.docs.forEach(doc => {
      const data = doc.data();
      if (data.user1Id) userConnections.add(data.user1Id);
    });

    // Get user's clubs
    const userMembershipsQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userId)
    );
    const userMembershipsSnapshot = await getDocs(userMembershipsQuery);
    const userClubIds = new Set<string>();
    userMembershipsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.entityId) userClubIds.add(data.entityId);
    });

    // Find other users with shared connections
    const partnerMap = new Map<string, {
      userId: string;
      sharedConnections: number;
      sharedClubs: number;
      lastInteraction: Date;
    }>();

    // Check each connection for shared connections
    for (const connectedUserId of Array.from(userConnections)) {
      try {
        // Get this user's connections
        const otherConnectionsQuery1 = query(
          collection(db, 'connections'),
          where('user1Id', '==', connectedUserId),
          where('status', '==', 'accepted')
        );
        const otherConnectionsQuery2 = query(
          collection(db, 'connections'),
          where('user2Id', '==', connectedUserId),
          where('status', '==', 'accepted')
        );

        const [otherSnap1, otherSnap2] = await Promise.all([
          getDocs(otherConnectionsQuery1),
          getDocs(otherConnectionsQuery2)
        ]);

        const otherConnections = new Set<string>();
        otherSnap1.docs.forEach(doc => {
          const data = doc.data();
          if (data.user2Id && data.user2Id !== userId) otherConnections.add(data.user2Id);
        });
        otherSnap2.docs.forEach(doc => {
          const data = doc.data();
          if (data.user1Id && data.user1Id !== userId) otherConnections.add(data.user1Id);
        });

        // Count shared connections
        let sharedCount = 0;
        otherConnections.forEach(otherUserId => {
          if (userConnections.has(otherUserId)) {
            sharedCount++;
          }
        });

        // Get this user's clubs
        const otherMembershipsQuery = query(
          collection(db, 'entityMembers'),
          where('userId', '==', connectedUserId)
        );
        const otherMembershipsSnapshot = await getDocs(otherMembershipsQuery);
        const otherClubIds = new Set<string>();
        otherMembershipsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.entityId) otherClubIds.add(data.entityId);
        });

        // Count shared clubs
        let sharedClubs = 0;
        otherClubIds.forEach(clubId => {
          if (userClubIds.has(clubId)) {
            sharedClubs++;
          }
        });

        // Get last interaction date
        const lastInteraction = new Date(0);
        // Try to find last interaction from connection or posts
        const connectionDoc = [...snapshot1.docs, ...snapshot2.docs].find(doc => {
          const data = doc.data();
          return (data.user1Id === connectedUserId || data.user2Id === connectedUserId);
        });
        if (connectionDoc) {
          const connData = connectionDoc.data();
          const updatedAt = connData.updatedAt?.toDate?.() || connData.updatedAt;
          if (updatedAt && updatedAt > lastInteraction) {
            lastInteraction.setTime(updatedAt.getTime());
          }
        }

        if (sharedCount > 0 || sharedClubs > 0) {
          const existing = partnerMap.get(connectedUserId);
          if (!existing || (sharedCount + sharedClubs) > (existing.sharedConnections + existing.sharedClubs)) {
            partnerMap.set(connectedUserId, {
              userId: connectedUserId,
              sharedConnections: sharedCount,
              sharedClubs: sharedClubs,
              lastInteraction: lastInteraction
            });
          }
        }
      } catch (err) {
        // Skip if user data not available
        continue;
      }
    }

    // Convert to array and get user profiles
    const partners: MutualGrowthPartner[] = [];
    for (const [partnerUserId, data] of Array.from(partnerMap.entries())) {
      try {
        const userProfileRef = doc(db, 'userProfiles', partnerUserId);
        const userProfileSnap = await getDoc(userProfileRef);
        if (userProfileSnap.exists()) {
          const profile = userProfileSnap.data();
          partners.push({
            userId: partnerUserId,
            userName: profile.displayName || profile.name || 'Onbekend',
            userPhoto: profile.photoURL,
            sharedConnections: data.sharedConnections,
            sharedClubs: data.sharedClubs,
            lastInteraction: data.lastInteraction
          });
        }
      } catch (err) {
        // Skip if profile not found
        continue;
      }
    }

    // Sort by relevance (most shared connections + clubs first)
    partners.sort((a, b) => {
      const aScore = a.sharedConnections + a.sharedClubs;
      const bScore = b.sharedConnections + b.sharedClubs;
      if (aScore !== bScore) return bScore - aScore;
      return b.lastInteraction.getTime() - a.lastInteraction.getTime();
    });

    // Return top 10
    return partners.slice(0, 10);
  } catch (error) {
    console.error('Error getting mutual growth partners:', error);
    return [];
  }
}

/**
 * Generate human-readable explanation
 */
function generateExplanation(factors: AIVisibilityFactors, score: number): string {
  const parts: string[] = [];

  if (score >= 80) {
    parts.push('Je bent zeer goed zichtbaar voor AI-zoekmachines.');
  } else if (score >= 60) {
    parts.push('Je bent goed zichtbaar voor AI-zoekmachines.');
  } else if (score >= 40) {
    parts.push('Je zichtbaarheid voor AI-zoekmachines is opbouwend.');
  } else {
    parts.push('Je zichtbaarheid voor AI-zoekmachines kan worden verbeterd.');
  }

  parts.push('Dit is gebaseerd op kwaliteit, context en samenwerking - geen likes, geen pay-to-win.');

  return parts.join(' ');
}
